import { Link } from "react-router-dom";
import { FileText, Plus } from "lucide-react";
import { isBookCollectionKey, knowledgeBaseLabel } from "@/lib/knowledgeBaseRegistry";
import {
  articlePath,
  formatKnowledgeDate,
  knowledgeStatusLabel,
  type KnowledgeArticle,
} from "@/lib/knowledgeModel";
import { agentsHref } from "@/lib/atwAgentsHost";
import {
  KnowledgeChrome,
  KnowledgeError,
  KnowledgeNeedsBase,
  knowledgePrimaryLinkClass,
  knowledgeTableWrapClass,
} from "@/pages/knowledge/KnowledgeChrome";
import { UploadMarkdownArticleButton } from "@/pages/knowledge/UploadMarkdownArticleButton";
import { useCustomBooks } from "@/pages/knowledge/useCustomBooks";
import { useKnowledgeArticles } from "@/pages/knowledge/useKnowledgeArticles";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";

function bookLabels(article: KnowledgeArticle, titles: Map<string, string>): string {
  const keys = article.collectionKeys.filter(isBookCollectionKey);
  if (keys.length === 0) return "None";
  return keys.map((key) => titles.get(key) || key).join(", ");
}

/** Articles in the active knowledge base from the shared live store. */
export function ArticlesPage() {
  const { articles, loading, error } = useKnowledgeArticles();
  const { books: customBooks } = useCustomBooks();
  const { active, hasBases, activeId } = useKnowledgeBases();
  const titles = new Map(customBooks.map((book) => [book.collectionKey, book.title] as const));

  if (!hasBases) {
    return (
      <KnowledgeChrome
        title="Articles"
        description="Markdown articles live inside a knowledge base. Create a knowledge base first."
        badge="Markdown"
        showBaseSwitcher={false}
      >
        <KnowledgeNeedsBase />
      </KnowledgeChrome>
    );
  }

  return (
    <KnowledgeChrome
      title="Articles"
      description={`Markdown articles in "${active ? knowledgeBaseLabel(active) : "this knowledge base"}". Upload a .md file or create one from scratch. Books are optional. Saves go to the same store One uses.`}
      badge="Markdown"
      actions={
        <>
          <UploadMarkdownArticleButton />
          <Link to={agentsHref("/knowledge/articles/new")} className={knowledgePrimaryLinkClass}>
            <Plus className="h-4 w-4" aria-hidden />
            New article
          </Link>
        </>
      }
    >
      {error ? <KnowledgeError message={error} /> : null}
      <div className={knowledgeTableWrapClass}>
        <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3 text-sm font-semibold text-white">
          <FileText className="h-4 w-4" aria-hidden />
          All articles
        </div>
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-zinc-900/50 dark:text-zinc-500">
            <tr>
              <th className="px-4 py-3 font-semibold">Title</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Books</th>
              <th className="px-4 py-3 text-right font-semibold">Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-slate-500 dark:text-zinc-500"
                >
                  Loading articles…
                </td>
              </tr>
            ) : articles.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center">
                  <p className="text-base font-semibold text-slate-900 dark:text-white">0 articles</p>
                  <p className="mx-auto mt-2 max-w-md text-sm text-slate-500 dark:text-zinc-500">
                    Nothing is created for you. Upload a .md file or use New article. A book or
                    library is not required.
                  </p>
                  <Link
                    to={agentsHref("/knowledge/articles/new")}
                    className="mt-4 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
                  >
                    <Plus className="h-4 w-4" aria-hidden />
                    New article
                  </Link>
                </td>
              </tr>
            ) : (
              articles.map((article) => (
                <tr
                  key={article.id}
                  className="border-b border-slate-100 dark:border-white/5"
                >
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                    <Link
                      to={agentsHref(articlePath(activeId, article.id))}
                      className="text-indigo-600 hover:underline dark:text-indigo-300"
                    >
                      {article.title || "Untitled"}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-zinc-300">
                    {knowledgeStatusLabel(article.status)}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-zinc-300">
                    {bookLabels(article, titles)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700 dark:text-zinc-300">
                    {formatKnowledgeDate(article.updatedAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </KnowledgeChrome>
  );
}
